<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { SelectTriggerProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'

export const selectTriggerVariants = cva(
  'inline-flex items-center justify-between gap-2 rounded-lg bg-white text-primal font-display border-0 ring-1 ring-inset ring-neutral-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 data-placeholder:[&>span]:text-placeholder disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate text-start [&_svg]:size-5 [&_svg]:shrink-0',
  {
    variants: {
      size: {
        xs: 'h-[24px] px-2 text-xs',
        sm: 'h-[32px] px-2 text-xs',
        md: 'h-[40px] px-2 text-base',
        lg: 'h-[52px] px-3 text-base',
        xl: 'h-[60px] px-4 text-lg',
      },
      error: {
        true: 'ring-error!',
        false: '',
      },
      width: {
        full: 'w-full',
        auto: 'w-auto',
      },
    },
    defaultVariants: {
      size: 'md',
      error: false,
      width: 'full',
    },
  },
)

export type SelectTriggerSize = NonNullable<VariantProps<typeof selectTriggerVariants>['size']>
export type SelectTriggerWidth = NonNullable<VariantProps<typeof selectTriggerVariants>['width']>

export type SelectTriggerWithSizeProps = SelectTriggerProps & {
  size?: SelectTriggerSize
  error?: boolean
  width?: SelectTriggerWidth
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import { reactiveOmit } from '@vueuse/core'
import { SelectIcon, SelectTrigger, useForwardProps } from 'reka-ui'
import { cn } from '#ui-kit/lib/utils'

const props = defineProps<SelectTriggerWithSizeProps>()

const delegatedProps = reactiveOmit(props, 'class', 'size', 'error', 'width')

const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <SelectTrigger
    v-bind="forwardedProps"
    :aria-invalid="!!props.error"
    :class="cn(selectTriggerVariants({ size: props.size, error: props.error, width: props.width }), props.class)"
  >
    <slot />
    <SelectIcon as-child>
      <ChevronDownIcon class="opacity-50" />
    </SelectIcon>
  </SelectTrigger>
</template>
